import { useState, useEffect } from 'react';
import { Check, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

// Paddle price IDs (sandbox)
const PADDLE_PRICES = {
  starter: {
    monthly: 'pri_01kb68wydzwf9039xq219t6n9t',
    annual: 'pri_01kb68wz49pj8btzcweqtb8h5k',
  },
  professional: {
    monthly: 'pri_01kb68wzjq9f3k261p48dwsrze',
    annual: 'pri_01kb68x00rmd3zjgd17pgwz68x',
  },
};

// Paddle client token (sandbox)
const PADDLE_CLIENT_TOKEN = 'test_5a885dc759cad74d1a5866d52c4';

interface PricingProps {
  onLogin: () => void;
  onRegister: () => void;
}

declare global {
  interface Window {
    Paddle: {
      Environment: {
        set: (env: 'sandbox' | 'production') => void;
      };
      Initialize: (config: { token: string; eventCallback?: (event: unknown) => void }) => void;
      Checkout: {
        open: (config: {
          items: { priceId: string; quantity: number }[];
          customData?: Record<string, unknown>;
          customer?: { email?: string };
          settings?: {
            displayMode?: 'overlay' | 'inline';
            theme?: 'light' | 'dark';
            locale?: string;
            successUrl?: string;
          };
        }) => void;
      };
    };
  }
}

export default function Pricing({ onLogin, onRegister }: PricingProps) {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [paddleLoaded, setPaddleLoaded] = useState(false);

  useEffect(() => {
    // Load Paddle.js
    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    script.onload = () => {
      if (window.Paddle) {
        window.Paddle.Environment.set('sandbox');
        window.Paddle.Initialize({
          token: PADDLE_CLIENT_TOKEN,
          eventCallback: (event: unknown) => {
            console.log('Paddle event:', event);
          },
        });
        setPaddleLoaded(true);
      }
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleSubscribe = (plan: 'starter' | 'professional') => {
    if (!paddleLoaded) {
      alert('Payment system is loading. Please try again in a moment.');
      return;
    }

    const priceId = PADDLE_PRICES[plan][billingCycle];
    
    window.Paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      settings: {
        displayMode: 'overlay',
        theme: 'dark',
        successUrl: `${import.meta.env.VITE_DASHBOARD_URL || 'https://dashboard.watchthesize.com'}?checkout=success`,
      },
    });
  };

  const pricingPlans = [
    {
      name: 'Free',
      price: { monthly: 0, annual: 0 },
      period: 'forever',
      description: 'Perfect for personal projects',
      servers: 1,
      features: [
        '1 server monitoring',
        'Basic disk usage alerts',
        '7-day history retention',
        'Email notifications',
        'Community support',
      ],
      cta: 'Use Free Tier',
      popular: false,
      plan: null as null,
    },
    {
      name: 'Starter',
      price: { monthly: 5.99, annual: 49.90 },
      period: billingCycle === 'monthly' ? '/month' : '/year',
      description: 'For growing teams and projects',
      servers: 10,
      features: [
        '10 server monitoring',
        'Smart alerts with thresholds',
        '30-day history retention',
        'Email + webhook notifications',
        'Priority email support',
        'API access',
      ],
      cta: 'Subscribe Now',
      popular: true,
      plan: 'starter' as const,
    },
    {
      name: 'Professional',
      price: { monthly: 10.99, annual: 99.90 },
      period: billingCycle === 'monthly' ? '/month' : '/year',
      description: 'For production environments',
      servers: 20,
      features: [
        '20 server monitoring',
        'Priority alerts',
        '90-day history retention',
        'All notification channels',
        'AI-powered insights',
        'Priority support',
        'Custom integrations',
      ],
      cta: 'Subscribe Now',
      popular: false,
      plan: 'professional' as const,
    },
  ];

  const faqs = [
    {
      question: 'How does the free tier work?',
      answer: 'The free tier lets you monitor 1 server indefinitely. It\'s perfect for trying out WatchTheSize or for personal projects. No credit card required.',
    },
    {
      question: 'Can I change plans at any time?',
      answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we\'ll prorate any differences.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, MasterCard, American Express) and PayPal through our secure payment provider, Paddle.',
    },
    {
      question: 'Is there a limit on data transfer?',
      answer: 'No, there are no data transfer limits. Our lightweight agent sends minimal data to optimize bandwidth usage.',
    },
    {
      question: 'Do you offer discounts for annual billing?',
      answer: 'Yes! Save up to 30% when you choose annual billing compared to monthly payments.',
    },
    {
      question: 'What happens if I exceed my server limit?',
      answer: 'You\'ll receive a notification when approaching your limit. You can easily upgrade to add more servers or remove inactive ones.',
    },
  ];

  // Calculate annual savings
  const starterMonthlyCost = 5.99 * 12;
  const starterAnnualCost = 49.90;
  const starterSavings = Math.round(((starterMonthlyCost - starterAnnualCost) / starterMonthlyCost) * 100);
  
  const proMonthlyCost = 10.99 * 12;
  const proAnnualCost = 99.90;
  const proSavings = Math.round(((proMonthlyCost - proAnnualCost) / proMonthlyCost) * 100);

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-lg shadow-lg shadow-violet-500/25">
              📊
            </div>
            <span className="text-xl font-bold text-white">WatchTheSize</span>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/#features" className="text-sm text-zinc-400 hover:text-white transition-colors">Features</Link>
            <Link to="/#how-it-works" className="text-sm text-zinc-400 hover:text-white transition-colors">How it Works</Link>
            <Link to="/pricing" className="text-sm text-white font-medium">Pricing</Link>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={onLogin}
              className="text-sm font-medium text-zinc-300 hover:text-white transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={onRegister}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-sm font-semibold text-white shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all hover:scale-[1.02]"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-900/20 via-transparent to-transparent" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-violet-500/10 rounded-full blur-[120px]" />
        
        <div className="relative max-w-4xl mx-auto text-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Simple, Transparent
            <br />
            <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">
              Pricing
            </span>
          </h1>
          
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Start free, upgrade when you need more. No hidden fees, no surprises. Cancel anytime.
          </p>

          {/* Billing Cycle Toggle */}
          <div className="inline-flex items-center gap-3 p-1.5 rounded-2xl bg-white/5 border border-white/10">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'annual'
                  ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Annual
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                billingCycle === 'annual'
                  ? 'bg-white/20 text-white'
                  : 'bg-emerald-500/20 text-emerald-400'
              }`}>
                Save up to {Math.max(starterSavings, proSavings)}%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {pricingPlans.map((plan, i) => (
              <div
                key={i}
                className={`relative p-8 rounded-2xl border ${
                  plan.popular
                    ? 'bg-gradient-to-b from-violet-500/10 to-fuchsia-500/10 border-violet-500/30'
                    : 'bg-white/[0.02] border-white/5'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-xs font-semibold text-white">
                    Most Popular
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                  <p className="text-sm text-zinc-400">{plan.description}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">
                      {plan.price[billingCycle] === 0 ? 'Free' : `$${plan.price[billingCycle]}`}
                    </span>
                    {plan.price[billingCycle] > 0 && (
                      <span className="text-zinc-400">{plan.period}</span>
                    )}
                  </div>
                  {plan.plan && billingCycle === 'annual' && (
                    <div className="mt-1 text-xs text-emerald-400">
                      Save {plan.plan === 'starter' ? starterSavings : proSavings}% vs monthly
                    </div>
                  )}
                  <div className="mt-2 text-sm text-violet-400 font-medium">
                    {plan.servers} server{plan.servers > 1 ? 's' : ''} included
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-zinc-300">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => plan.plan ? handleSubscribe(plan.plan) : onRegister()}
                  className={`w-full py-3 rounded-xl font-semibold transition-all ${
                    plan.popular
                      ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 hover:scale-[1.02]'
                      : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-zinc-500 mt-8">
            Use the free tier to try WatchTheSize. Upgrade anytime as your needs grow.
          </p>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 px-6 bg-zinc-900/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
            Compare Plans
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Feature</th>
                  <th className="text-center py-4 px-4 text-sm font-medium text-zinc-400">Free</th>
                  <th className="text-center py-4 px-4 text-sm font-medium text-violet-400">Starter</th>
                  <th className="text-center py-4 px-4 text-sm font-medium text-zinc-400">Professional</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Servers', free: '1', starter: '10', pro: '20' },
                  { feature: 'History Retention', free: '7 days', starter: '30 days', pro: '90 days' },
                  { feature: 'Email Notifications', free: true, starter: true, pro: true },
                  { feature: 'Webhook Notifications', free: false, starter: true, pro: true },
                  { feature: 'API Access', free: false, starter: true, pro: true },
                  { feature: 'AI Insights', free: false, starter: false, pro: true },
                  { feature: 'Priority Support', free: false, starter: true, pro: true },
                  { feature: 'Custom Integrations', free: false, starter: false, pro: true },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-4 px-4 text-sm text-zinc-300">{row.feature}</td>
                    <td className="py-4 px-4 text-center">
                      {typeof row.free === 'boolean' ? (
                        row.free ? (
                          <Check className="w-5 h-5 text-emerald-400 mx-auto" />
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )
                      ) : (
                        <span className="text-sm text-zinc-300">{row.free}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center bg-violet-500/5">
                      {typeof row.starter === 'boolean' ? (
                        row.starter ? (
                          <Check className="w-5 h-5 text-emerald-400 mx-auto" />
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )
                      ) : (
                        <span className="text-sm text-violet-300">{row.starter}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {typeof row.pro === 'boolean' ? (
                        row.pro ? (
                          <Check className="w-5 h-5 text-emerald-400 mx-auto" />
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )
                      ) : (
                        <span className="text-sm text-zinc-300">{row.pro}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="p-6 rounded-2xl bg-white/[0.02] border border-white/5"
              >
                <h3 className="text-lg font-semibold text-white mb-2">{faq.question}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="p-12 rounded-3xl bg-gradient-to-br from-violet-500/10 via-fuchsia-500/10 to-pink-500/10 border border-white/10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto mb-8">
              Start with our free tier and upgrade as your needs grow. No credit card required.
            </p>
            <button
              onClick={onRegister}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-base font-semibold text-white shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all hover:scale-[1.02]"
            >
              Start Free
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-sm">
                📊
              </div>
              <span className="text-sm font-semibold text-white">WatchTheSize</span>
            </Link>
            
            <div className="flex items-center gap-6 text-sm text-zinc-400">
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/refund" className="hover:text-white transition-colors">Refund</Link>
              <a href="#" className="hover:text-white transition-colors">Support</a>
            </div>
            
            <p className="text-sm text-zinc-500">
              © {new Date().getFullYear()} WatchTheSize. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
